/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/4g4b5dg/screen/54225329
 */

import React from 'react';
import loadable from '@loadable/component';
import { Document, Font, Image, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import isNil from 'ramda/src/isNil';
import range from 'ramda/src/range';
import values from 'ramda/src/values';
import Icon from '@components/@core/Icon';
import { inPage, scorecards } from '~/services/util';

const Header = loadable(() => import('@components/PdfHeader'), { ssr: false });

const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        alignItems: 'stretch',
    },
    listHeader: {
        display: 'flex',
        flexDirection: 'row',
        paddingBottom: '10px',
        paddingTop: '10px',
        borderBottomWidth: 1.3,
        borderBottomColor: 'gray',
        borderBottomStyle: 'solid',
    },
    listItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginLeft: 10,
        marginRight: 10,
        paddingTop: 10,
        paddingBottom: 10,
        borderBottomWidth: 1.3,
        borderBottomColor: 'gray',
        borderBottomStyle: 'solid',
        minHeight: 25,
        maxHeight: 100,
    },
    pageNumbers: {
        position: 'absolute',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        bottom: 10,
        left: 0,
        right: 0,
        textAlign: 'center',
        margin: '10px',
        fontSize: 10,
        fontFamily: 'Roboto',
        fontWeight: 'normal',
    },
    ratings: { height: '13px', width: '13px', color: 'black', border: '2px', borderStyle: 'solid' },
    fontStyle2: { fontSize: '12', fontWeight: 'light', fontFamily: 'Roboto' },
    fontStyle3: { fontSize: '10', fontWeight: '500', fontFamily: 'Roboto' },
    titleHead: { fontSize: 14, fontWeight: 'bold', fontFamily: 'Roboto' },
    listTitle: { fontSize: 10, fontWeight: 'normal', fontFamily: 'Roboto' },
    listItemData: { fontSize: 10, fontWeight: 'light', fontFamily: 'Roboto' },
});

Font.register({
    family: 'Roboto',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'normal',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'bold',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'light',
        },
    ],
});

const rating = (scoreRate) => {
    const ratingArray = range(0, 5);
    return (
        <View style={{ flex: 1, flexDirection: 'row', textAlign: 'left', display: 'flex-start' }}>
            {ratingArray.map((i, index) => (
                <Image src={i >= Math.ceil(scoreRate) ? '/images/ratings_active.png' : '/images/star.png'} key={index + 'rate'} style={styles.ratings} />
            ))}
            <Text style={styles.listTitle}>&nbsp;{scoreRate}</Text>
        </View>
    );
};

const showOrgChart = (data, column, dateStart, dateEnd, currentPage, perPage) => (
    <>
        <View>
            <Text style={[styles.fontStyle2, { paddingBottom: '10px' }]}>Average Score</Text>
            {rating(data?.stats?.completed_average_star_score?.stars)}
            <Text style={[styles.fontStyle2, { paddingTop: '10px' }]}>
                {data?.stats?.reviews_completed?.count}&#47;{data?.stats?.scorecards?.count}&nbsp;Reviews completed
            </Text>
        </View>
        <View wrap style={{ display: 'flex' }}>
            <View style={styles.listHeader} fixed>
                <View style={{ flex: 4 }}>
                    <Text style={styles.fontStyle3}>Department</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={[styles.fontStyle3, { textAlign: 'right', paddingRight: '10px' }]}>Team</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={[styles.fontStyle3, { textAlign: 'right', paddingRight: '10px' }]}>Employees</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={[styles.fontStyle3, { textAlign: 'right', paddingRight: '10px' }]}>Completed</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={[styles.fontStyle3]}>Score</Text>
                </View>
            </View>
        </View>

        {data?.departments?.map((item, index) => {
            const { department, totals } = item;
            const employees = values(item?.employees);
            const pageBreak = column.length === 0 ? index + 1 : index + perPage;
            const isVisible = column.filter((item) => Object.keys(item).includes(department.name || department))[0];
            const isPagination = isVisible && Object.values(isVisible)[0];

            return (
                <>
                    <View style={styles.listHeader} key={index} wrap break={pageBreak % 13 === 0}>
                        <View style={{ flex: 4 }}>
                            <Text style={styles.fontStyle2}>{department.name || department}</Text>
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={[styles.fontStyle2, { textAlign: 'right', paddingRight: '10px' }]}>0</Text>
                        </View>
                        <View style={{ flex: 2 }}>
                            <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{totals?.employees?.count}</Text>
                        </View>
                        <View style={{ flex: 2 }}>
                            <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{totals?.reviews_completed?.count}</Text>
                        </View>
                        <View style={{ flex: 2, paddingLeft: '10px' }}>{rating(totals?.completed_average_star_score?.stars)}</View>
                    </View>
                    {isVisible && showOrgEmployees(employees, dateStart, dateEnd, isPagination.page, isPagination.per)}
                </>
            );
        })}
        <Text fixed style={styles.pageNumbers} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    </>
);

const showOrgEmployees = (data, dateStart, dateEnd, currentPage, perPage) => (
    <View>
        <View style={[styles.listHeader, { marginLeft: '10px' }]}>
            <View style={{ flex: 2 }}>
                <Text style={styles.listTitle}>Employee</Text>
            </View>
            <View style={{ flex: 3 }}>
                <Text style={styles.listTitle}>ScoreCard</Text>
            </View>
            <View style={{ flex: 1, marginRight: '5px' }}>
                <Text style={styles.listTitle}>Actual</Text>
            </View>
            <View style={{ flex: 2 }}>
                <Text style={styles.listTitle}>Status</Text>
            </View>
            <View style={{ flex: 2, marginRight: '10px' }}>
                <Text style={styles.listTitle}>score</Text>
            </View>
        </View>
        {data?.map(
            (emp, index) =>
                inPage(index, currentPage, perPage) && (
                    <View style={styles.listItem} key={`crd${index}`} wrap>
                        <View style={{ flex: 2 }}>
                            <Text style={styles.listItemData}>{`${emp?.profile?.first_name || ''} ${emp?.profile?.last_name || ''}`}</Text>
                        </View>
                        <View style={{ flex: 3 }}>
                            <Text style={styles.listItemData}>
                                {emp?.performance?.status == 2 ? values(emp?.performance.data.scorecards)[0].title : emp?.scorecard ? emp?.scorecard?.title : 'No Scorecard Assigned'}
                            </Text>
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.listItemData}>{scorecards(emp, dateStart, dateEnd, 'actuals')}</Text>
                        </View>
                        <View style={{ flex: 2 }}>
                            <Text style={styles.listItemData}>{scorecards(emp, dateStart, dateEnd)}</Text>
                        </View>
                        <View style={{ flex: 2 }}>
                            {emp?.performance?.status === 2 ? (
                                <>
                                    {emp?.performance?.data?.completed_average_star_rating === 'N/A' ? (
                                        <Text style={styles.listItemData}>Na (Per Manager)</Text>
                                    ) : (
                                        rating(emp?.performance?.data?.completed_average_star_rating)
                                    )}
                                </>
                            ) : !emp?.scorecard ? (
                                <Text style={styles.listItemData}>No Scorecard</Text>
                            ) : (
                                <Text style={styles.listItemData}>Incomplete</Text>
                            )}
                        </View>
                    </View>
                )
        )}
    </View>
);

const showEmployee = (data, dateStart, dateEnd) => (
    <>
        <View style={{ marginLeft: 10, marginRight: 10 }} fixed>
            <View style={{ paddingBottom: 10 }}>
                <View style={{ flex: 1 }}>
                    <Text style={styles.titleHead}>Performance Report</Text>
                </View>
            </View>
            <View style={styles.listHeader}>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listTitle}>Employee</Text>
                </View>
                <View style={{ flex: 4 }}>
                    <Text style={styles.listTitle}>ScoreCard</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listTitle}>Score</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listTitle}>Commitments</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.listTitle}>Status</Text>
                </View>
            </View>
        </View>
        {data.users.map((item, index) => {
            const scorecard = item.scorecards?.length > 0 ? item.scorecards[0] : null;

            return (
                <View style={styles.listItem} key={`crd${index}`} wrap break={(index + 1) % 13 === 0}>
                    <View style={{ flex: 2 }}>
                        <Text style={styles.listItemData}>{`${item.user.profile.first_name} ${item.user.profile.last_name}`}</Text>
                    </View>
                    <View style={{ flex: 4 }}>
                        <Text style={styles.listItemData}> {!scorecard ? 'No Scorecard' : scorecard.title}</Text>
                    </View>
                    <View style={{ flex: 2 }}>
                        {isNil(item.performance) ? (
                            <Text style={styles.listItemData}>Incomplete</Text>
                        ) : (
                            <Text>
                                {item.performance.status === 2 ? (
                                    <Text>
                                        {item.performance.data && 'N/A' !== item.performance.data.average_star_rating ? (
                                            <Text style={styles.listItemData}>{item.performance.data.average_star_rating}</Text>
                                        ) : (
                                            <Text style={styles.listItemData}>Na (Per Manager)</Text>
                                        )}
                                    </Text>
                                ) : (
                                    <Text style={styles.listItemData}>Incomplete</Text>
                                )}
                            </Text>
                        )}
                    </View>
                    <View style={{ flex: 2 }}>
                        {item?.performance?.status !== 2 && <Text style={styles.listItemData}>Incomplete</Text>}
                        {item?.performance?.status === 2 && !scorecard && <Text style={styles.listItemData}>N/A</Text>}
                        {item?.performance?.status === 2 && scorecard && (
                            <View>
                                <View>
                                    <Text style={styles.listItemData}>Learning: {item.performance.trainings.length}</Text>
                                </View>
                                <View>
                                    <Text style={styles.listItemData}>Task: {item.performance.tasks.length}</Text>
                                </View>
                            </View>
                        )}
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.listItemData}>{scorecards(item, dateStart, dateEnd)}</Text>
                    </View>
                </View>
            );
        })}
        <Text fixed style={styles.pageNumbers} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    </>
);

const showActuals = (actualsData, activeDept, data) => (
    <View>
        <View style={{ paddingBottom: 20 }}>
            <View style={{ flex: 1 }}>
                <Text style={styles.titleHead}>Performance Report</Text>
            </View>
        </View>
        <Text style={[styles.fontStyle2, { paddingBottom: '10px' }]}>Average Score</Text>
        {rating(data?.stats?.completed_average_star_score?.stars)}
        {Object.keys(actualsData).map((key, index) => (
            <View key={`sa${index}`} wrap break={(index + 1) % 13 === 0}>
                <View style={styles.listItem}>
                    <Text style={{ marginLeft: 0, marginRight: 10, fontSize: 10 }}>{key}</Text>
                </View>
                {activeDept.includes(key) && (
                    <View style={{ marginLeft: 20, marginRight: 10 }} fixed>
                        <View style={styles.listItem}>
                            <View style={{ flex: 4 }}>
                                <Text style={styles.listTitle}>Employees</Text>
                            </View>
                            <View style={{ flex: 2 }}>
                                <Text style={styles.listTitle}>Scorecard quotas</Text>
                            </View>
                            <View style={{ flex: 2 }}>
                                <Text style={styles.listTitle}>Health Plan Quotas</Text>
                            </View>
                            <View style={{ flex: 2 }}>
                                <Text style={styles.listTitle}>Certificate Quotas</Text>
                            </View>
                            <View style={{ flex: 2 }}>
                                <Text style={styles.listTitle}>Total</Text>
                            </View>
                        </View>
                    </View>
                )}
                {actualsData[key]?.users.map(
                    (userList, index) =>
                        activeDept.includes(key) && (
                            <View style={{ marginLeft: 20, marginRight: 10 }} key={`sae${index}`} wrap break={(index + 1) % 13 === 0}>
                                <View style={styles.listItem}>
                                    <View style={{ flex: 4 }}>
                                        <Text style={styles.fontStyle3}>{userList?.name}</Text>
                                    </View>
                                    <View style={{ flex: 2 }}>
                                        <Text style={styles.fontStyle3}>
                                            {userList?.report?.scorecard_quotas?.saved || 0}/{userList?.report?.scorecard_quotas?.assigned || 0}
                                        </Text>
                                        <Text style={styles.fontStyle3}>{userList?.report?.scorecard_quotas?.percent || 0}%</Text>
                                    </View>
                                    <View style={{ flex: 2 }}>
                                        <Text style={styles.fontStyle3}>
                                            {userList?.report?.career_quotas?.saved || 0}/{userList?.report?.career_quotas?.assigned || 0}
                                        </Text>
                                        <Text style={styles.fontStyle3}>{userList?.report?.career_quotas?.percent || 0}%</Text>
                                    </View>
                                    <View style={{ flex: 2 }}>
                                        <Text style={styles.fontStyle3}>
                                            {userList?.report?.certification_quotas?.saved || 0}/{userList?.report?.certification_quotas?.assigned || 0}
                                        </Text>
                                        <Text style={styles.fontStyle3}>{userList?.report?.certification_quotas?.percent || 0}%</Text>
                                    </View>
                                    <View style={{ flex: 2 }}>
                                        <Text style={styles.fontStyle3}>{userList?.report?.all_quotas?.percent || 0}%</Text>
                                    </View>
                                </View>
                            </View>
                        )
                )}
            </View>
        ))}
    </View>
);

export const PerformanceReviewPdf = ({ data, actuals, activeDept, companyId, active, column, page, per, dateStart, dateEnd }) => {
    const header = `Reports - Performance ${active}`;
    const date = moment(dateStart).format('MMM DD') + ' - ' + moment(dateEnd).format('MMM DD');
    const actualsData = actuals?.companies[companyId]?.departments;
    pdf(
        <Document>
            <Page size="letter" style={styles.container} wrap>
                <Header header={header} date={date} fixed style={{ paddingTop: 10, paddingBottom: 20 }} />
                {active === 'employees' && showEmployee(data, dateStart, dateEnd)}
                {active === 'chart' && showOrgChart(data, column, dateStart, dateEnd, page, per)}
                {active === 'saveactuals' && showActuals(actualsData, activeDept, data)}
            </Page>
        </Document>
    )
        .toBlob()
        .then((e) => {
            const url = URL.createObjectURL(e);
            window.open(url, '__blank');
        });
};

const PdfIcon = ({ data, active, actuals, activeDept, companyId, column, page, per, dateStart, dateEnd }) => {
    const handlePdf = () => {
        PerformanceReviewPdf({
            data,
            actuals,
            activeDept,
            companyId,
            dateStart,
            dateEnd,
            active,
            column,
            page,
            per,
        });
    };

    return (
        <div className="ml-3 cursor-pointer">
            <Icon name="fas fa-print" size={16} color="#343f4b" onClick={handlePdf} />
        </div>
    );
};

export default PdfIcon;
